import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { Users, CheckCircle, Clock, Target, Pencil } from "lucide-react";

type PolicyQuiz = Database["public"]["Tables"]["policy_quizzes"]["Row"];
type QuizAttempt = Database["public"]["Tables"]["quiz_attempts"]["Row"];
type PolicyRow = Database["public"]["Tables"]["policies"]["Row"];
type ProfileRow = Database["public"]["Tables"]["profiles"]["Row"];

interface QuizWithMeta extends PolicyQuiz {
  policy?: PolicyRow | null;
  attempts?: QuizAttempt[];
}

export default function Quizzes() {
  const { profile, roles, hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quizzes, setQuizzes] = useState<QuizWithMeta[]>([]);
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);

  const isPolicyPersonnel = hasAnyRole(["creator", "reviewer", "approver", "admin"]);

  const canEditQuiz = (quiz: QuizWithMeta) => {
    if (!profile?.id || !quiz.policy) return false;
    const p = quiz.policy;
    return (
      p.created_by === profile.id ||
      p.assigned_reviewers?.includes(profile.id) ||
      p.assigned_approvers?.includes(profile.id) ||
      hasAnyRole(["admin"])
    );
  };

  useEffect(() => {
    if (profile?.company_id) {
      void fetchData();
    }
  }, [profile?.company_id]);

  const fetchData = async () => {
    if (!profile?.company_id) return;
    setLoading(true);
    try {
      const [{ data: quizzesData }, { data: attemptsData }] = await Promise.all([
        supabase
          .from("policy_quizzes")
          .select("*")
          .eq("company_id", profile.company_id)
          .eq("is_active", true),
        supabase
          .from("quiz_attempts")
          .select("*")
          .eq("company_id", profile.company_id),
      ]);

      let policyMap: Record<string, PolicyRow> = {};
      if (quizzesData && quizzesData.length > 0) {
        const policyIds = Array.from(new Set(quizzesData.map((q) => q.policy_id)));
        const { data: policies } = await supabase
          .from("policies")
          .select("*")
          .in("id", policyIds);
        if (policies) {
          policyMap = policies.reduce((acc, p) => {
            acc[p.id] = p;
            return acc;
          }, {} as Record<string, PolicyRow>);
        }
      }

      let companyProfiles: ProfileRow[] = [];
      if (isPolicyPersonnel) {
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("*")
          .eq("company_id", profile.company_id);
        companyProfiles = profilesData || [];
      }

      const attemptsByQuiz: Record<string, QuizAttempt[]> = {};
      (attemptsData || []).forEach((a) => {
        if (!attemptsByQuiz[a.quiz_id]) attemptsByQuiz[a.quiz_id] = [];
        attemptsByQuiz[a.quiz_id].push(a);
      });

      const quizzesWithMeta: QuizWithMeta[] =
        quizzesData?.map((q) => ({
          ...q,
          policy: policyMap[q.policy_id],
          attempts: attemptsByQuiz[q.id] || [],
        })) || [];

      setQuizzes(quizzesWithMeta);
      setAttempts(attemptsData || []);
      setProfiles(companyProfiles);
    } catch (error) {
      console.error("Error loading quizzes:", error);
      setQuizzes([]);
      setAttempts([]);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const attemptsByQuizAndUser = useMemo(() => {
    const map: Record<string, Record<string, QuizAttempt>> = {};
    attempts.forEach((attempt) => {
      if (!map[attempt.quiz_id]) map[attempt.quiz_id] = {};
      const existing = map[attempt.quiz_id][attempt.user_id];
      if (!existing || existing.completed_at < attempt.completed_at) {
        map[attempt.quiz_id][attempt.user_id] = attempt;
      }
    });
    return map;
  }, [attempts]);

  const myAttempts = useMemo(() => {
    if (!profile?.id) return {};
    const map: Record<string, QuizAttempt> = {};
    attempts.forEach((attempt) => {
      if (attempt.user_id !== profile.id) return;
      const existing = map[attempt.quiz_id];
      if (!existing || existing.completed_at < attempt.completed_at) {
        map[attempt.quiz_id] = attempt;
      }
    });
    return map;
  }, [attempts, profile?.id]);

  const pendingQuizzes = quizzes.filter((q) => !myAttempts[q.id]);
  const completedQuizzes = quizzes.filter((q) => !!myAttempts[q.id]);

  const completionRate = useMemo(() => {
    if (!isPolicyPersonnel || profiles.length === 0 || quizzes.length === 0) return 0;
    const totalEmployees = profiles.length;
    const totalSlots = totalEmployees * quizzes.length;

    let completedSlots = 0;
    quizzes.forEach((quiz) => {
      const byUser = attemptsByQuizAndUser[quiz.id] || {};
      completedSlots += Object.keys(byUser).length;
    });

    if (totalSlots === 0) return 0;
    return Math.round((completedSlots / totalSlots) * 100);
  }, [isPolicyPersonnel, profiles.length, quizzes.length, attemptsByQuizAndUser]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-medium text-foreground">Policy Quizzes</h1>
          <p className="text-muted-foreground">
            Short, flashcard-style quizzes to help your team internalize key policies.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isPolicyPersonnel && (
            <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="w-4 h-4" />
              <span>
                Company-wide completion:{" "}
                <span className="font-medium text-foreground">{completionRate}%</span>
              </span>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      ) : quizzes.length === 0 ? (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle>No quizzes yet</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground max-w-xl">
              Once a policy is approved, creators, reviewers, and approvers can generate a quiz
              from the policy page. Quizzes will appear here for everyone in the company to take.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="pending" className="space-y-4">
          <TabsList>
            <TabsTrigger value="pending">
              To Do
              <Badge variant="outline" className="ml-2">
                {pendingQuizzes.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="completed">
              Completed
              <Badge variant="outline" className="ml-2">
                {completedQuizzes.length}
              </Badge>
            </TabsTrigger>
            {isPolicyPersonnel && (
              <TabsTrigger value="stats">
                Team Stats
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            {pendingQuizzes.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-10 flex flex-col items-center justify-center gap-2">
                  <Target className="w-8 h-8 text-muted-foreground mb-1" />
                  <p className="text-sm text-muted-foreground">
                    You&apos;ve completed all available quizzes. Nice work!
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingQuizzes.map((quiz, index) => (
                  <motion.div
                    key={quiz.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04 }}
                  >
                    <Card className="h-full flex flex-col">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center justify-between gap-2">
                          <span className="line-clamp-1">{quiz.title}</span>
                          <Badge variant="outline">New</Badge>
                        </CardTitle>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                          {quiz.description}
                        </p>
                      </CardHeader>
                      <CardContent className="pt-0 flex-1 flex flex-col justify-between gap-3">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>
                            {Array.isArray((quiz as any).questions)
                              ? (quiz as any).questions.length
                              : "10"}{" "}
                            questions
                          </span>
                          {quiz.policy && (
                            <span className="truncate">
                              From: <span className="font-medium">{quiz.policy.title}</span>
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2 mt-1">
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() => navigate(`/quiz/${quiz.id}`)}
                          >
                            Start Quiz
                          </Button>
                          {canEditQuiz(quiz) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/quiz/${quiz.id}/edit`);
                              }}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4">
            {completedQuizzes.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-10 flex flex-col items-center justify-center gap-2">
                  <Clock className="w-8 h-8 text-muted-foreground mb-1" />
                  <p className="text-sm text-muted-foreground">
                    You haven&apos;t completed any quizzes yet.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {completedQuizzes.map((quiz, index) => {
                  const attempt = myAttempts[quiz.id];
                  const score = attempt?.score ?? 0;
                  return (
                    <motion.div
                      key={quiz.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.04 }}
                    >
                      <Card className="h-full flex flex-col">
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center justify-between gap-2">
                            <span className="line-clamp-1">{quiz.title}</span>
                            <Badge
                              variant={score >= 80 ? "default" : score >= 60 ? "secondary" : "outline"}
                            >
                              {score}%
                            </Badge>
                          </CardTitle>
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                            {quiz.description}
                          </p>
                        </CardHeader>
                        <CardContent className="pt-0 flex-1 flex flex-col justify-between gap-3">
                          <div className="space-y-2">
                            <Progress value={score} className="h-1.5" />
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>
                                {attempt?.correct_answers}/{attempt?.total_questions} correct
                              </span>
                              {quiz.policy && (
                                <span className="truncate">
                                  From: <span className="font-medium">{quiz.policy.title}</span>
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 mt-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => navigate(`/quiz/${quiz.id}`)}
                            >
                              Retake Quiz
                            </Button>
                            {canEditQuiz(quiz) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/quiz/${quiz.id}/edit`);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {isPolicyPersonnel && (
            <TabsContent value="stats" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="lg:col-span-1">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Team Coverage
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Overall completion</span>
                        <span className="font-medium text-foreground">{completionRate}%</span>
                      </div>
                      <Progress value={completionRate} className="h-1.5" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Completion is calculated across all employees and active quizzes in your
                      company.
                    </p>
                  </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Quiz-by-Quiz Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {quizzes.map((quiz) => {
                      const byUser = attemptsByQuizAndUser[quiz.id] || {};
                      const completedCount = Object.keys(byUser).length;
                      const totalEmployees = profiles.length || 1;
                      const rate = Math.round((completedCount / totalEmployees) * 100);
                      const avgScore =
                        Object.values(byUser).reduce((sum, a) => sum + (a.score || 0), 0) /
                        (completedCount || 1);

                      return (
                        <div
                          key={quiz.id}
                          className="border rounded-lg p-3 flex flex-col gap-2"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{quiz.title}</div>
                              {quiz.policy && (
                                <div className="text-xs text-muted-foreground truncate">
                                  From: {quiz.policy.title}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                              <span>
                                {completedCount}/{totalEmployees} completed
                              </span>
                              <span>•</span>
                              <span>Avg score {Math.round(avgScore)}%</span>
                              {canEditQuiz(quiz) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2"
                                  onClick={() => navigate(`/quiz/${quiz.id}/edit`)}
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <Progress value={rate} className="h-1.5" />
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}

